import { createFileRoute, redirect } from '@tanstack/react-router';
import { SessionRecoveryAlert } from '@/components/auth/session-recovery-alert';
import { logger } from '@/lib/utils/logger';
import { resolveIndexRoute } from '@/lib/auth/route-auth-decisions';

type IndexSearch = {
  auth?: string;
  code?: string;
};

// Hand-rolled like _public/class/$classId.tsx: this route-def file is in the
// eager chunk for every page, and a zod import here pinned all of zod into the
// root preload set. Mirrors the previous
// z.object({ auth: z.string().optional().catch(undefined), code: z.coerce.string().optional().catch(undefined) }).passthrough():
// - auth keeps only genuine strings; anything else (TanStack's JSON.parse
//   search deserializer can yield numbers/booleans/arrays) falls back to absent,
//   exactly as .catch(undefined) did.
// - code coerces any defined value with String(), exactly like z.coerce.string()
//   (magic-link codes arrive JSON-parsed as numbers, e.g. ?code=123).
// - every other param passes through untouched, like .passthrough().
const parseIndexSearch = (
  search: Record<string, unknown>
): IndexSearch & Record<string, unknown> => {
  const parsed: IndexSearch & Record<string, unknown> = { ...search };
  if (typeof search.auth === 'string') {
    parsed.auth = search.auth;
  } else {
    delete parsed.auth;
  }
  if (search.code !== undefined) {
    parsed.code = String(search.code);
  } else {
    delete parsed.code;
  }
  return parsed;
};

export const Route = createFileRoute('/')({
  validateSearch: parseIndexSearch,
  beforeLoad: ({ context, search }) => {
    const decision = resolveIndexRoute(context.authState, search);
    if (decision.kind === 'redirect') {
      logger.info('IndexRoute', 'Redirecting from root entry', {
        destination: decision.to,
      });
      throw redirect({
        to: decision.to,
        search: decision.search,
        replace: true,
      });
    }
  },
  component: IndexPage,
});

function IndexPage() {
  const { authState } = Route.useRouteContext();
  return authState === 'unavailable' ? <SessionRecoveryAlert /> : null;
}
